import { useState } from 'react'
import ProgressBar from './ProgressBar.jsx'
import HsBadge from './HsBadge.jsx'
import GscBadge from './GscBadge.jsx'
import OwnerPicker from './OwnerPicker.jsx'
import SupportPicker from './SupportPicker.jsx'
import EditableValue from './EditableValue.jsx'
import CommentBubble from './CommentBubble.jsx'
import ReactionBubble from './ReactionBubble.jsx'
import WeeklySummary from './WeeklySummary.jsx'
import ActionItems from './ActionItems.jsx'
import EditingPresence from './EditingPresence.jsx'
import { calcPct, fmt, statusColor, toTitleCase } from '../data/format.js'
import { useGoalsCrud } from '../mutations/useGoalsCrud.js'
import { useGscGoalSync, isGscHomepageGoal } from '../hooks/useGscGoalSync.js'

/**
 * Card for a top-level goal. Shows:
 *   - Title + HubSpot badge (if hsSync)
 *   - Current / target with progress percentage
 *   - DRI and owner chips
 *   - Weekly update text (or placeholder)
 *   - Expand/collapse for sub-goals (rendered as children)
 *
 * READ-ONLY for now — edit mode, comments, and mutation hooks land
 * in a future session once the data layer is real.
 */
export default function GoalCard({ goal, pillarColor, pillarName, goalIndex, children, isFirst }) {
  const [expanded, setExpanded] = useState(true)
  const [hovered, setHovered] = useState(false)
  const { removeGoal } = useGoalsCrud()
  const pct = calcPct(goal.current, goal.target, goal.lowerIsBetter)
  const color = pillarColor || statusColor(pct)
  const hasSubgoals = Array.isArray(goal.subgoals) && goal.subgoals.length > 0

  // If this is the Search Console-backed Homepage Ranking goal, keep
  // goal.current in sync with the live GSC position. The hook is a no-op
  // for every other goal.
  const isGscGoal = isGscHomepageGoal(goal)
  const { data: gscData } = useGscGoalSync({
    enabled: isGscGoal,
    pillarName,
    goalIndex,
    goal,
  })

  // SupportPicker handles parsing goal.owner internally — it filters out
  // the DRI, renders each remaining name as a removable chip, and writes
  // back as a comma-separated string when the user adds/removes supporters.

  const handleDelete = () => {
    if (
      confirm(
        `Delete the goal "${goal.goal}"?\n\n` +
          `This will also remove any weekly summary, action items, and comments tied to it.`
      )
    ) {
      removeGoal({ pillarName, goalIndex })
    }
  }

  return (
    <div
      data-tour={isFirst ? 'first-goal' : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: '16px 18px',
        marginBottom: 12,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              marginBottom: 4,
            }}
          >
            {hasSubgoals && (
              <button
                onClick={() => setExpanded((x) => !x)}
                aria-label={expanded ? 'Collapse' : 'Expand'}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 11,
                  color: 'var(--muted-light)',
                  padding: 0,
                }}
              >
                {expanded ? '▼' : '▶'}
              </button>
            )}
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>
              <EditableValue
                pillarName={pillarName}
                goalIndex={goalIndex}
                field="goal"
                value={goal.goal}
                inputType="text"
                formatter={(v) => toTitleCase(v)}
              />
            </span>
            {goal.hsSync && <HsBadge />}
            {isGscGoal && (
              <GscBadge
                asOf={gscData?.asOf}
                impressions={gscData?.impressions}
                clicks={gscData?.clicks}
              />
            )}
            <CommentBubble label={goal.goal} type="Goal" />
            <ReactionBubble label={goal.goal} />
            <EditingPresence pillarName={pillarName} goalIndex={goalIndex} />
          </div>
          {goal.description && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--muted)',
                fontStyle: 'italic',
                marginTop: 2,
                paddingLeft: hasSubgoals ? 18 : 0,
              }}
            >
              {goal.description}
            </div>
          )}
        </div>

        {/* DRI + Owner chips + delete on hover */}
        <div
          data-tour="goal-owners"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <OwnerPicker
            pillarName={pillarName}
            goalIndex={goalIndex}
            field="dri"
            value={goal.dri}
            label="DRI"
          />
          <SupportPicker
            pillarName={pillarName}
            goalIndex={goalIndex}
            dri={goal.dri}
            ownerField={goal.owner}
            // At the goal level everyone beyond the DRI is "Support".
            // "Owner" is reserved for sub-goal rows (where a specific
            // person is on the hook for that one line item).
            label="Support"
          />
          <button
            type="button"
            onClick={handleDelete}
            title={`Delete goal "${goal.goal}"`}
            aria-label="Delete goal"
            style={{
              width: 22,
              height: 22,
              border: 'none',
              background: 'transparent',
              color: 'var(--muted-light)',
              fontSize: 13,
              cursor: 'pointer',
              opacity: hovered ? 0.8 : 0,
              transition: 'opacity 0.15s, color 0.15s, background 0.15s',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              marginLeft: 2,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1'
              e.currentTarget.style.color = '#dc2626'
              e.currentTarget.style.background = '#fee2e2'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = hovered ? '0.8' : '0'
              e.currentTarget.style.color = 'var(--muted-light)'
              e.currentTarget.style.background = 'transparent'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Progress row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div style={{ flex: 1 }}>
          <ProgressBar pct={pct} color={color} />
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-secondary)',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          <EditableValue
            pillarName={pillarName}
            goalIndex={goalIndex}
            field="current"
            value={goal.current}
            unit={goal.unit}
            formatter={(v) => (isGscGoal ? `#${v}` : fmt(v, goal.unit))}
            readonly={Boolean(goal.hsSync) || isGscGoal}
          />
          <span style={{ color: 'var(--muted-light)', fontWeight: 500 }}>
            {' '}
            /{' '}
            {/* Target always editable — HubSpot pulls current, not target */}
            <EditableValue
              pillarName={pillarName}
              goalIndex={goalIndex}
              field="target"
              value={goal.target}
              unit={goal.unit}
              formatter={(v) => (isGscGoal ? `#${v}` : fmt(v, goal.unit))}
            />
          </span>
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color,
            background: color + '18',
            padding: '2px 8px',
            borderRadius: 99,
            minWidth: 40,
            textAlign: 'center',
          }}
        >
          {pct}%
        </div>
      </div>

      {/* Weekly summary: TLDR / Wins / Flags — three structured fields */}
      <WeeklySummary pillarName={pillarName} goalIndex={goalIndex} />

      {/* Action items — checklist of tasks tied to this goal.
          Default owner = the goal's DRI (not the person creating the item),
          since the DRI is typically on the hook for execution. */}
      <ActionItems
        pillarName={pillarName}
        goalIndex={goalIndex}
        goalLabel={goal.goal}
        defaultOwner={goal.dri || goal.owner || '—'}
      />

      {/* Sub-goals render as children */}
      {hasSubgoals && expanded && children && (
        <div style={{ marginTop: 12 }}>{children}</div>
      )}
    </div>
  )
}
